import {
  ClickableCardSurface,
  SimpleErrorNotification,
  ConfirmInputModal,
  ActionCard,
} from "shared/components";
import { ShopWindow } from "iconoir-react";
import { Flex } from "styled-system/jsx";
import { Card, Text, useToastContext } from "@ttc3k/trekker";
import { useTranslation } from "react-i18next";
import { useDangerZone } from "apps/directory/Operator/views";
import { useState } from "react";
import { useOperatorContext } from "apps/directory/Operator";
import { EnumOperatorOperationalStatus } from "gql/generated.ts";
import { formatDate, getMultilingualString } from "shared/utils";

export const DangerZone = () => {
  const { t } = useTranslation();
  const { toastFactory } = useToastContext();
  const { operator } = useOperatorContext();
  const { handleUpdateOperationalStatus, updateLoading } = useDangerZone({
    operatorEntityId: operator?._entityId,
    onSuccess: (operatorName) => {
      toastFactory.create({
        title: t("core:SUCCESS"),
        description: t("directory:OPERATOR.PROFILE.FORM_SUCCESS", {
          name: operatorName,
        }),
      });
      handleSetIsModalOpen();
    },
    onError: () => {
      toastFactory.create({
        title: t("core:SUCCESS"),
        description: t("directory:OPERATOR.ERROR.UPDATING_OPERATOR"),
      });
    },
  });

  const currentStatus = !operator?.operationalStatus
    ? "CLOSE"
    : operator.operationalStatus !== EnumOperatorOperationalStatus.Open
      ? "REOPEN"
      : "CLOSE";

  const [selectedStatus, setSelectedStatus] =
    useState<EnumOperatorOperationalStatus | null>(null);

  const handleSelectStatus = (status: EnumOperatorOperationalStatus) => {
    setSelectedStatus(status);
  };

  const [isDangerZoneCloseModalOpen, setIsDangerZoneCloseModalOpen] =
    useState(false);

  const handleSetIsModalOpen = () => {
    setIsDangerZoneCloseModalOpen(!isDangerZoneCloseModalOpen);
  };

  if (!operator) return <SimpleErrorNotification />;

  return (
    <>
      <ActionCard
        title={t(
          `directory:OPERATOR.PROFILE.DANGER_ZONE.OPERATIONAL_STATUS.${currentStatus}.TITLE`,
        )}
        subText={t(
          `directory:OPERATOR.PROFILE.DANGER_ZONE.OPERATIONAL_STATUS.${currentStatus}.DESCRIPTION`,
        )}
        buttonProps={{
          visual: "destructive",
          children:
            t(
              `directory:OPERATOR.PROFILE.DANGER_ZONE.OPERATIONAL_STATUS.${currentStatus}.BUTTON_TEXT`,
            ) + "...",
          onClick: handleSetIsModalOpen,
        }}
        surfaceProps={{ borderColor: "border.error" }}
        bodyProps={{ bg: "bg.error.lightest" }}
        textProps={{ color: "text.error.dark" }}
      />
      <ConfirmInputModal
        isOpen={isDangerZoneCloseModalOpen}
        onClose={handleSetIsModalOpen}
        title={t(
          `directory:OPERATOR.PROFILE.DANGER_ZONE.OPERATIONAL_STATUS.${currentStatus}.TITLE`,
        )}
        HeaderIcon={ShopWindow}
        cancelButtonProps={{
          onClick: handleSetIsModalOpen,
        }}
        saveButtonCopy={t(
          `directory:OPERATOR.PROFILE.DANGER_ZONE.OPERATIONAL_STATUS.${currentStatus}.BUTTON_TEXT`,
        )}
        saveButtonProps={{
          visual: currentStatus === "CLOSE" ? "destructive" : "success",
          isLoading: updateLoading,
          disabled: currentStatus === "CLOSE" && !selectedStatus,
          onClick: () =>
            handleUpdateOperationalStatus(
              currentStatus === "CLOSE" && selectedStatus
                ? selectedStatus
                : EnumOperatorOperationalStatus.Open,
            ),
        }}
        textToConfirm={getMultilingualString(operator.name)}
      >
        {currentStatus === "CLOSE" && (
          <Flex flexDir={"column"} gap={"250"}>
            <Text color={"text.warning.dark"}>
              {t(
                "directory:OPERATOR.PROFILE.DANGER_ZONE.OPERATIONAL_STATUS.CLOSE.MODAL.DESCRIPTION",
              )}
            </Text>
            <Flex flexDir={"column"} gap={"100"}>
              <Text visual={"bodySemiBold"}>
                {t(
                  "directory:OPERATOR.PROFILE.DANGER_ZONE.OPERATIONAL_STATUS.CLOSE.MODAL.CLOSURE_TYPE.TITLE",
                )}
              </Text>
              <Text color={"text.mid"}>
                {t(
                  "directory:OPERATOR.PROFILE.DANGER_ZONE.OPERATIONAL_STATUS.CLOSE.MODAL.CLOSURE_TYPE.DESCRIPTION",
                )}
              </Text>
            </Flex>
            <Flex gap={"150"}>
              <ClickableCardSurface
                data-selected={
                  selectedStatus ===
                  EnumOperatorOperationalStatus.TemporarilyClosed
                }
                onClick={() =>
                  handleSelectStatus(
                    EnumOperatorOperationalStatus.TemporarilyClosed,
                  )
                }
              >
                <Card.Body>
                  <Text visual={"bodySemiBold"}>
                    {t(
                      "directory:OPERATOR.PROFILE.DANGER_ZONE.OPERATIONAL_STATUS.CLOSE.MODAL.CLOSURE_TYPE.TEMPORARILY_CLOSED",
                    )}
                  </Text>
                </Card.Body>
              </ClickableCardSurface>
              <ClickableCardSurface
                data-selected={
                  selectedStatus ===
                  EnumOperatorOperationalStatus.PermanentlyClosed
                }
                onClick={() =>
                  handleSelectStatus(
                    EnumOperatorOperationalStatus.PermanentlyClosed,
                  )
                }
              >
                <Card.Body>
                  <Text visual={"bodySemiBold"}>
                    {t(
                      "directory:OPERATOR.PROFILE.DANGER_ZONE.OPERATIONAL_STATUS.CLOSE.MODAL.CLOSURE_TYPE.PERMANENTLY_CLOSED",
                    )}
                  </Text>
                </Card.Body>
              </ClickableCardSurface>
            </Flex>
          </Flex>
        )}
        {currentStatus === "REOPEN" && (
          <Text color={"text.mid"}>
            {t(
              "directory:OPERATOR.PROFILE.DANGER_ZONE.OPERATIONAL_STATUS.REOPEN.MODAL.DESCRIPTION",
              { date: formatDate(operator.updatedDateISO) },
            )}
          </Text>
        )}
      </ConfirmInputModal>
    </>
  );
};
